export interface BreadcrumbItem {
  title: string;
  path?: string;
}

// Video, Audio and Images crumbs must link to the consolidated search URLs; the old landing URLs (/video, /video/footage, ...) now 308-redirect to them
const VIDEO_SEARCH_PATH = '/video/search';
const AUDIO_SEARCH_PATH = '/audio/search';
const IMAGES_SEARCH_PATH = '/images/search';
const TEMPLATES_SEARCH_PATH = '/templates/search';
const MOTION_GRAPHICS_SEARCH_PATH = '/motion-graphics/search';

// media-type query value => crumb title
const AUDIO_MEDIA_TYPE_TITLES: Readonly<Record<string, string>> = {
  music: 'Music',
  'sound-effects': 'Sound Effects',
};

// media-type query value => crumb title
const IMAGE_MEDIA_TYPE_TITLES: Readonly<Record<string, string>> = {
  photos: 'Photos',
  snapshots: 'Snapshots',
  vectors: 'Vectors',
  illustrations: 'Illustrations',
};

// Utility functions
function pathSegments(location: string): string[] {
  const path = location.split('?')[0];
  return path.split('/').filter(Boolean);
}

function titleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function formatTitle(title: string): string {
  const words = title.split('-').filter((w) => w.toLowerCase() !== 'all');
  return words
    .map((word) => {
      if (
        word.toLowerCase() === 'and' ||
        (/^[A-Z]{2,3}$/.test(word) && word.length <= 3)
      ) {
        return word;
      }
      return titleCase(word);
    })
    .join(' ');
}

function pluckSearchTerm(location: string): string | null {
  const path = location.split('?')[0];
  const match = path.match(/\/.*?\/search\/(.*)/);
  if (match) {
    return formatTitle(decodeURIComponent(match[1]));
  }
  return null;
}

function getQueryParam(location: string, param: string): string | null {
  const queryString = location.split('?')[1] || '';
  const params = new URLSearchParams(queryString);
  return params.get(param);
}

function pluckVideoContributor(location: string): string | null {
  const segments = pathSegments(location);
  const [first, second, contributor] = segments;

  if (first === 'video' && second === 'portfolio' && contributor) {
    return decodeURIComponent(contributor);
  }
  return null;
}

function mediaTypeBreadcrumb(
  location: string,
  searchPath: string,
  mediaTypeTitles: Readonly<Record<string, string>>
): BreadcrumbItem | null {
  const mediaType = getQueryParam(location, 'media-type');
  const title = mediaType ? mediaTypeTitles[mediaType] : undefined;
  if (!mediaType || !title) {
    return null;
  }
  return { title, path: `${searchPath}?media-type=${mediaType}` };
}

// Crumb links must match the target page's canonical, which is always absolute
function withAbsolutePaths(
  breadcrumbs: BreadcrumbItem[],
  origin: string
): BreadcrumbItem[] {
  return breadcrumbs.map((breadcrumb) =>
    breadcrumb.path
      ? { ...breadcrumb, path: `${origin}${breadcrumb.path}` }
      : breadcrumb
  );
}

// Breadcrumb generation functions
function generateVideoBreadcrumbs(location: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Video', path: VIDEO_SEARCH_PATH },
  ];
  const segments = pathSegments(location);

  // Details page
  if (segments[1] === 'stock') {
    // For client-side fallback, we can't fetch stock item data
    // Show generic breadcrumb - PHP will provide actual title
    breadcrumbs.push({ title: 'Stock Item' });
    return breadcrumbs;
  }

  // Collections page
  if (segments[1] === 'collections') {
    breadcrumbs.push({ title: 'Collections', path: '/video/collections' });
    return breadcrumbs;
  }

  // Portfolio/contributor
  const contributor = pluckVideoContributor(location);
  if (contributor) {
    breadcrumbs.push({ title: contributor });
    return breadcrumbs;
  }

  // Search and category pages
  const searchTerm = pluckSearchTerm(location);

  // Footage (/video/...) is the default state of the video search hub, so only Backgrounds and Templates get a crumb
  if (location.startsWith('/motion-graphics/')) {
    breadcrumbs.push({
      title: 'Motion Graphics',
      path: MOTION_GRAPHICS_SEARCH_PATH,
    });
  } else if (location.startsWith('/templates/')) {
    breadcrumbs.push({ title: 'Templates', path: TEMPLATES_SEARCH_PATH });
  }

  if (searchTerm) {
    breadcrumbs.push({ title: searchTerm });
  }

  return breadcrumbs;
}

function generateAudioBreadcrumbs(location: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Audio', path: AUDIO_SEARCH_PATH },
  ];
  const segments = pathSegments(location);

  // Details page
  if (segments[1] === 'stock') {
    // For client-side fallback, we can't fetch stock item data
    // Show generic breadcrumb - PHP will provide actual title
    breadcrumbs.push({ title: 'Stock Item' });
    return breadcrumbs;
  }

  // Collections page
  if (segments[1] === 'collections') {
    breadcrumbs.push({ title: 'Collections', path: '/audio/collections' });
    return breadcrumbs;
  }

  // Search pages
  if (location.startsWith(AUDIO_SEARCH_PATH)) {
    const searchTerm = pluckSearchTerm(location);
    const contentTypeBreadcrumb = mediaTypeBreadcrumb(
      location,
      AUDIO_SEARCH_PATH,
      AUDIO_MEDIA_TYPE_TITLES
    );

    if (contentTypeBreadcrumb) {
      breadcrumbs.push(contentTypeBreadcrumb);
    }

    if (searchTerm) {
      breadcrumbs.push({ title: searchTerm });
    }
  }

  return breadcrumbs;
}

function generateImageBreadcrumbs(location: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Images', path: IMAGES_SEARCH_PATH },
  ];
  const segments = pathSegments(location);

  // Details page
  if (segments[1] === 'stock') {
    // For client-side fallback, we can't fetch stock item data
    // Show generic breadcrumb - PHP will provide actual title
    breadcrumbs.push({ title: 'Stock Item' });
    return breadcrumbs;
  }

  // Collections page
  if (segments[1] === 'collections') {
    breadcrumbs.push({ title: 'Collections', path: '/images/collections' });
    return breadcrumbs;
  }

  // Search pages
  if (location.startsWith(IMAGES_SEARCH_PATH)) {
    const searchTerm = pluckSearchTerm(location);
    const contentTypeBreadcrumb = mediaTypeBreadcrumb(
      location,
      IMAGES_SEARCH_PATH,
      IMAGE_MEDIA_TYPE_TITLES
    );

    if (contentTypeBreadcrumb) {
      breadcrumbs.push(contentTypeBreadcrumb);
    }

    if (searchTerm) {
      breadcrumbs.push({ title: searchTerm });
    }
  }

  return breadcrumbs;
}

function generateBreadcrumbsWithRelativePaths(
  location: string
): BreadcrumbItem[] | null {
  const segments = pathSegments(location);
  const root = segments[0] || '';
  const contentType = segments[1] || null;

  const homeBreadcrumb: BreadcrumbItem = { title: 'Home', path: '/' };

  // Video pages
  if (
    root === 'video' &&
    contentType &&
    ['templates', 'footage', 'motion-backgrounds'].includes(contentType)
  ) {
    return null;
  }

  if (
    !location.startsWith('/video/upgrade-my-plan') &&
    !location.startsWith('/video/change-my-plan') &&
    !location.startsWith('/video/member/cancel') &&
    (root.includes('video') ||
      root === 'motion-graphics' ||
      root === 'templates')
  ) {
    return [homeBreadcrumb, ...generateVideoBreadcrumbs(location)];
  }

  // Audio pages
  if (
    root === 'audio' &&
    contentType &&
    ['music', 'sound-effects'].includes(contentType)
  ) {
    return null;
  }

  if (root === 'audio') {
    return [homeBreadcrumb, ...generateAudioBreadcrumbs(location)];
  }

  // Image pages
  if (
    root === 'images' &&
    contentType &&
    ['illustrations', 'vectors', 'photos'].includes(contentType)
  ) {
    return null;
  }

  if (root === 'images') {
    return [homeBreadcrumb, ...generateImageBreadcrumbs(location)];
  }

  return null;
}

// Main generation function
export function generateBreadcrumbs(
  location: string,
  origin: string
): BreadcrumbItem[] | null {
  const breadcrumbs = generateBreadcrumbsWithRelativePaths(location);
  return breadcrumbs ? withAbsolutePaths(breadcrumbs, origin) : null;
}
